import i18nFactory from '@yandex-int/i18n';
import { compact, includes, isArray, mergeWith } from 'lodash';

import { PhraseAdjustmentItem } from '../../components/PhraseAdjustment/PhraseAdjustment';
import { PricesWizardExportData } from '../../components/PricesWizardInspector/PricesWizardInspector';
import {
    getPhraseFilterCriteriaContainers,
    PhraseCriteriaKey,
    StatisticsByAllGoals
} from '../../declarations/filter/filter';
import { flattenContainer, getStrictExpressions } from '../../service/FilterHelpers/FilterHelpers';
import { getNumberValue, parseNumber } from '../../service/FormatHelpers/FormatHelpers';
import { mapPhraseWithMinusWordsToString } from '../../service/PhrasesHelpers/PhrasesHelpers';
import { ClientInfo } from '../../typings/common';
import {
    CampaignPlatformVisibility,
    CampaignsCalcTypeFilter,
    ClientData,
    Phrase,
    PhraseBidChange,
    PhraseChange,
    PhraseEditPermissions,
    PokazometerData,
    TrafficVolumeDataItem
} from '../../typings/domain';
import {
    Criteria,
    FilterExpression,
    FilterPartialExpression,
    GoalStatsQueryFilter,
    PhrasesQueryFilter,
    StatsQueryFilter
} from '../../typings/filter';
import {
    adsPhraseRowFragment,
    campaignFlatStrategyFragment,
    ChangeModeInput,
    ClientFeaturesFragment,
    FindAndReplaceKeywordsPreview,
    GdCampaignPlatform,
    GdFindAndReplaceKeywordFieldInput,
    GdFindAndReplaceKeywordsInput,
    GdSetAutoBidsInput,
    GdSetBidsInput,
    GdShowConditionAutobudgetPriority,
    GdShowConditionPrimaryStatusInput,
    GdShowConditionTypeInput,
    GdUpdateKeywordsItemInput,
    PokazometerDataFragment,
    RefinePhraseQuery,
    showConditionsRowFragment
} from '../../typings/graphql-grid';
import { GridSourceParams } from '../../typings/grid';
import {
    ReplaceOption,
    SearchAndReplaceInspectorState,
    SearchAndReplacePreviewData,
    SearchByRadioButton
} from '../../typings/searchAndReplace';
import { mapRowsPhraseToBanners } from '../banner/banner';
import { getValueFromApiFormat, mapStatsDataToStats } from '../common/common';
import { FilterCriteriaMappings, textToPhraseFilterExpression } from '../filter/filter';
import { mapRowGoals } from '../goal/goal';
import { mapRowsToGroupsTruncatedBaseInfo } from '../group/group';

import * as keyset from './phrase.i18n';

const i18n = i18nFactory(keyset);

export function mapRowsToPhrases(
    phraseRows: showConditionsRowFragment[],
    clientInfo: ClientData,
    params: GridSourceParams,
    features: ClientFeaturesFragment
): Phrase[] {
    return phraseRows.reduce((res: Phrase[], row) => {
        const { status, aggregatedStatusInfo, aggregatedStatus } = row;
        const campaignFlatStrategy = row.adGroup.campaign.strategy;
        const rowGoalStats = row.goalStats || [];
        switch (row.__typename) {
            case 'GdKeyword': {
                const isPhraseEditable = row.access.canEdit;
                const platformVisibility = getCampaignPlatformVisibility(campaignFlatStrategy);

                const editableFields = isPhraseEditable
                    ? getEditablePhraseFields(campaignFlatStrategy)
                    : {
                          priceEditable: false,
                          priceContextEditable: false,
                          autobudgetPriorityEditable: false
                      };

                const bannerRows: adsPhraseRowFragment[] = row.mainAd ? [row.mainAd] : [];

                const [mainBanner] = mapRowsPhraseToBanners(bannerRows, clientInfo, params, row.adGroup);

                res.push({
                    id: String(row.id),
                    type: row.type,
                    groupId: String(row.adGroupId),
                    group: mapRowsToGroupsTruncatedBaseInfo(row.adGroup, clientInfo, params),
                    isSuspended: status.suspended,
                    showsForecast: getNumberValue(row.showsForecast),
                    phrase: row.keyword,
                    minusWords: row.minusKeywords,
                    // TODO: DIRECT-82567 - бага в автостратегиях в сетях. Price для сетей должен быть null. Правка в задаче
                    price: platformVisibility.isSearchVisibility ? getNumberValue(row.price) : undefined,
                    wCtr: getNumberValue(row.weightedCtr),
                    hasPhraseTrafaretAuction: row.usePhraseTrafaretAuction,
                    // TODO: DIRECT-82567 - бага в автостратегиях в сетях. AuctionData для сетей должен быть null. Правка в задаче
                    trafficVolumeData:
                        platformVisibility.isSearchVisibility && row.auctionData
                            ? row.auctionData.auctionDataItems.map(
                                  ({ bid, trafficVolume, amnestyPrice, showInTable }): TrafficVolumeDataItem => {
                                      return {
                                          bid: parseNumber(bid.toString()),
                                          trafficVolume: parseNumber(trafficVolume.toString()),
                                          amnestyPrice: parseNumber(amnestyPrice.toString()),
                                          showInTable: Boolean(showInTable)
                                      };
                                  }
                              )
                            : undefined,
                    priceContext: getNumberValue(row.priceContext),
                    autobudgetPriority: getValueFromApiFormat(row.autobudgetPriority),
                    stats: mapStatsDataToStats(row.stats),
                    goalStats: mapRowGoals(rowGoalStats),
                    status: status.primaryStatus,
                    ...editableFields,
                    ...getCampaignPlatformVisibility(campaignFlatStrategy),
                    access: row.access,
                    pokazometerData:
                        row.pokazometerData !== null ? mapPokazometerDataFromApiFormat(row.pokazometerData) : undefined,
                    mainBanner,
                    bannersTotalCount: row.adsInAdGroupCount,
                    aggregatedStatusInfo:
                        aggregatedStatusInfo !== null
                            ? {
                                  status: aggregatedStatusInfo.status,
                                  reasons: compact(aggregatedStatusInfo.reasons),
                                  counters: {}
                              }
                            : undefined,
                    aggregatedStatus
                });
                break;
            }
            case 'GdRelevanceMatch': {
                const isPhraseEditable = row.access.canEdit;
                const platformVisibility = getCampaignPlatformVisibility(campaignFlatStrategy);
                const editableFields = isPhraseEditable
                    ? getEditablePhraseFields(campaignFlatStrategy)
                    : {
                          priceEditable: false,
                          priceContextEditable: false,
                          autobudgetPriorityEditable: false
                      };

                const bannerRows: adsPhraseRowFragment[] = row.mainAd ? [row.mainAd] : [];

                const [mainBanner] = mapRowsPhraseToBanners(bannerRows, clientInfo, params, row.adGroup);

                res.push({
                    id: String(row.id),
                    type: row.type,
                    groupId: String(row.adGroupId),
                    group: mapRowsToGroupsTruncatedBaseInfo(row.adGroup, clientInfo, params),
                    isSuspended: status.suspended,
                    showsForecast: undefined,
                    phrase: `---${i18n('relevance-match')}`,
                    minusWords: [],
                    // TODO: DIRECT-82567 - бага в автостратегиях в сетях. Price для сетей должен быть null. Правка в задаче
                    price: platformVisibility.isSearchVisibility ? getNumberValue(row.price) : undefined,
                    wCtr: 0,
                    hasPhraseTrafaretAuction: false,
                    // TODO: DIRECT-82567 - бага в автостратегиях в сетях. AuctionData для сетей должен быть null. Правка в задаче
                    trafficVolumeData: undefined,
                    priceContext: getNumberValue(row.priceContext),
                    autobudgetPriority: getValueFromApiFormat(row.autobudgetPriority),
                    stats: mapStatsDataToStats(row.stats),
                    goalStats: mapRowGoals(rowGoalStats),
                    status: status.primaryStatus,
                    ...editableFields,
                    ...getCampaignPlatformVisibility(campaignFlatStrategy),
                    access: row.access,
                    pokazometerData: undefined,
                    mainBanner,
                    bannersTotalCount: row.adsInAdGroupCount,
                    aggregatedStatusInfo:
                        aggregatedStatusInfo !== null
                            ? {
                                  status: aggregatedStatusInfo.status,
                                  reasons: compact(aggregatedStatusInfo.reasons),
                                  counters: {}
                              }
                            : undefined,
                    aggregatedStatus
                });
                break;
            }
            default:
                break;
        }

        return res;
    }, []);
}

const mappings = new FilterCriteriaMappings<PhrasesQueryFilter & StatsQueryFilter & GoalStatsQueryFilter>();
const { toNumber, toEnum } = mappings.valueMappings;

const phraseCriteriaMappings: { [key in PhraseCriteriaKey]: (expression: FilterExpression) => PhrasesQueryFilter } = {
    id: mappings.includes('showConditionIdIn', 'showConditionIdNotIn'),
    keyword: mappings.contains('keywordContains', 'keywordNotContains'),
    status: mappings.equality('showConditionStatusIn', toEnum(GdShowConditionPrimaryStatusInput)),
    autobudgetPriority: mappings.equality('autobudgetPriorityIn', toEnum(GdShowConditionAutobudgetPriority)),
    price: mappings.comparison('minPrice', 'maxPrice', toNumber, false),
    priceContext: mappings.comparison('minPriceContext', 'maxPriceContext', toNumber, false),
    cost: mappings.comparison('minCost', 'maxCost', toNumber),
    revenue: mappings.comparison('minRevenue', 'maxRevenue', toNumber),
    profitability: mappings.comparison('minProfitability', 'maxProfitability', toNumber),
    avgGoalCost: (expression: FilterExpression): PhrasesQueryFilter => {
        if (expression.additionalCriteriaKey && expression.additionalCriteriaKey !== StatisticsByAllGoals) {
            return mappings.comparison(
                'minCostPerAction',
                'maxCostPerAction',
                toNumber,
                false,
                expression.additionalCriteriaKey
            )(expression);
        }

        return mappings.comparison('minAvgGoalCost', 'maxAvgGoalCost', toNumber)(expression);
    },
    avgClickCost: mappings.comparison('minAvgClickCost', 'maxAvgClickCost', toNumber),
    shows: mappings.comparison('minShows', 'maxShows', toNumber),
    avgCpmCost: mappings.comparison('minCpmPrice', 'maxCpmPrice', toNumber),
    clicks: mappings.comparison('minClicks', 'maxClicks', toNumber),
    ctr: mappings.comparison('minCtr', 'maxCtr', toNumber),
    goals: (expression: FilterExpression): PhrasesQueryFilter => {
        if (expression.additionalCriteriaKey && expression.additionalCriteriaKey !== StatisticsByAllGoals) {
            return mappings.comparison('minGoals', 'maxGoals', toNumber, false, expression.additionalCriteriaKey)(
                expression
            );
        }

        return mappings.comparison('minGoals', 'maxGoals', toNumber)(expression);
    },
    conversionRate: (expression: FilterExpression): PhrasesQueryFilter => {
        if (expression.additionalCriteriaKey && expression.additionalCriteriaKey !== StatisticsByAllGoals) {
            return mappings.comparison(
                'minConversionRate',
                'maxConversionRate',
                toNumber,
                false,
                expression.additionalCriteriaKey
            )(expression);
        }

        return mappings.comparison('minConversionRate', 'maxConversionRate', toNumber)(expression);
    },
    bounceRate: mappings.comparison('minBounceRate', 'maxBounceRate', toNumber)
};

const expressionToPhrasesQueryFilter = (expression: FilterExpression): PhrasesQueryFilter => {
    return phraseCriteriaMappings[expression.criteriaKey](expression);
};

export const mapPhraseFilterToApiFormat = (
    options: ClientInfo,
    expressions: FilterPartialExpression[] = [],
    calcType?: CampaignsCalcTypeFilter
): PhrasesQueryFilter => {
    const criteria: Criteria[] = [];

    getPhraseFilterCriteriaContainers(options, calcType === CampaignsCalcTypeFilter.CPM).forEach(({ criteria: c }) =>
        criteria.push(...flattenContainer(c))
    );

    return getStrictExpressions(expressions, criteria, textToPhraseFilterExpression)
        .map(expressionToPhrasesQueryFilter)
        .reduce(
            (obj, otherObj) =>
                mergeWith(obj, otherObj, (objValue, srcValue) => {
                    if (isArray(objValue)) {
                        return objValue.concat(srcValue);
                    }

                    return undefined;
                }),
            {
                typeIn: [GdShowConditionTypeInput.KEYWORD, GdShowConditionTypeInput.RELEVANCE_MATCH]
            }
        );
};

export const mapPokazometerDataFromApiFormat = (pokazometerData: PokazometerDataFragment): PokazometerData => {
    return {
        maxShows: Number(pokazometerData.maxShows),
        allCostsAndClicks: pokazometerData.allCostsAndClicks.map(val => {
            return {
                clicks: Number(val.clicks),
                price: Number(val.price)
            };
        }),
        priceForCoverage: pokazometerData.priceForCoverage
            .map(val => {
                return {
                    coverage: Number(val.coverage),
                    price: Number(val.price)
                };
            })
            .sort((a, b) => b.coverage - a.coverage)
    };
};

export const mapAutobudgetPriorityTypeToPerlApiFormat = (
    autobudgetPriority: GdShowConditionAutobudgetPriority
): number => {
    const AutobudgetPriorityTypePerlDict = {
        [GdShowConditionAutobudgetPriority.LOW]: 1,
        [GdShowConditionAutobudgetPriority.MEDIUM]: 3,
        [GdShowConditionAutobudgetPriority.HIGH]: 5
    };

    return AutobudgetPriorityTypePerlDict[autobudgetPriority];
};

function getCampaignPlatformVisibility(strategy: campaignFlatStrategyFragment): CampaignPlatformVisibility {
    const { platform } = strategy;

    const campaignPlatformSearchVisibility = [GdCampaignPlatform.SEARCH, GdCampaignPlatform.BOTH];

    const campaignPlatformContextVisibility = [GdCampaignPlatform.CONTEXT, GdCampaignPlatform.BOTH];

    return {
        isSearchVisibility: includes(campaignPlatformSearchVisibility, platform),
        isContextVisibility: includes(campaignPlatformContextVisibility, platform)
    };
}

function getEditablePhraseFields(strategy: campaignFlatStrategyFragment): PhraseEditPermissions {
    const { platform } = strategy;
    const permissions = {
        priceEditable: false,
        priceContextEditable: false,
        autobudgetPriorityEditable: false
    };

    if (!strategy.isAutoBudget) {
        permissions.priceEditable = [GdCampaignPlatform.SEARCH, GdCampaignPlatform.BOTH].includes(platform);
        permissions.priceContextEditable = [GdCampaignPlatform.CONTEXT, GdCampaignPlatform.BOTH].includes(platform);
        // для стратегии ручное управление редактируем цену в сети
        // только при раздельном управлении
        if (
            platform === GdCampaignPlatform.BOTH &&
            strategy.__typename === 'GdCampaignStrategyManual' &&
            !strategy.separateBidding
        ) {
            permissions.priceContextEditable = false;
        }
    } else {
        permissions.autobudgetPriorityEditable = true;
    }

    return permissions;
}

export function mapSearchAndReplaceFormValueToApiFormat(
    { value, preview }: SearchAndReplaceInspectorState,
    phrasesIds: string[]
): GdFindAndReplaceKeywordsInput {
    const fields = [];

    const cacheKey = preview && preview.cacheKey;

    if (value.searchByRadioButtonValue === SearchByRadioButton.PHRASE) {
        fields.push(GdFindAndReplaceKeywordFieldInput.KEYWORD);
    } else {
        fields.push(GdFindAndReplaceKeywordFieldInput.MINUS_KEYWORDS);
    }

    return {
        changeText: value.replaceValue,
        searchText: value.searchValue,
        searchOptions: {
            matchCase: value.matchCase,
            onlyWholeWords: value.wholeWords
        },
        fields,
        changeMode: mapStringToChangeModeInput(value.replaceOptionValue),
        keywordIds: phrasesIds,
        cacheKey
    };
}

function mapStringToChangeModeInput(value: ReplaceOption): ChangeModeInput {
    switch (value) {
        case ReplaceOption.REPLACE:
            return ChangeModeInput.REPLACE;

        case ReplaceOption.ADD_TO_END:
            return ChangeModeInput.POSTFIX;

        case ReplaceOption.ADD_TO_START:
            return ChangeModeInput.PREFIX;

        default:
            throw new Error('Не поддерживаемый тип ChangeModeInput');
    }
}

export function mapToPhraseReplacePreviewData({
    getFindAndReplaceKeywordsPreview
}: FindAndReplaceKeywordsPreview): SearchAndReplacePreviewData {
    const items = getFindAndReplaceKeywordsPreview.rowset.map(
        ({ keyword, changedKeyword, minusKeywords, changedMinusKeywords }) => ({
            oldValue: mapPhraseWithMinusWordsToString(keyword, minusKeywords),
            newValue: mapPhraseWithMinusWordsToString(
                changedKeyword !== null ? changedKeyword : keyword,
                changedMinusKeywords || minusKeywords
            )
        })
    );

    return {
        items,
        totalCount: getFindAndReplaceKeywordsPreview.totalCount,
        cacheKey: getFindAndReplaceKeywordsPreview.cacheKey
    };
}

export function mapPricesMasterDataToBidsInputApiData(
    pricesExportData: PricesWizardExportData,
    phrasesIds?: string[]
): GdSetBidsInput {
    const { simple } = pricesExportData;

    if (simple === undefined) {
        throw Error('Данные по единой ставке не выставлены');
    }

    return {
        showConditionIds: phrasesIds,
        exactPriceSearch: simple.exactPriceSearch,
        exactPriceContext: simple.exactPriceContext
    } as GdSetBidsInput;
}

export function mapPricesMasterDataToAutoBidsInputApiData(
    pricesExportData: PricesWizardExportData,
    phrasesIds: string[],
    mcbannerPhrasesIds: string[]
): [GdSetAutoBidsInput, GdSetAutoBidsInput] {
    const { extended } = pricesExportData;

    if (extended === undefined) {
        throw Error('Данные по мастеру ставок не выставлены');
    }

    const { net, search, mcbanner } = extended;

    const searchPlaceholder = {
        setMaximum: true,
        targetTrafficVolume: 100,
        maxBid: '300',
        increasePercentage: 30
    };

    return [
        search || net
            ? {
                  keywordIds: phrasesIds,
                  searchByTrafficVolume: search && {
                      maxBid: search.priceLimit,
                      setMaximum: search.volume === 'max',
                      targetTrafficVolume: search.volume === 'max' ? 100 : search.volume, // при setMaximum: true это поле игнорируется, но бэкэнд просит что-нибудь все-таки присылать
                      increasePercentage: search.increasePercentage
                  },
                  networkByCoverage: net && {
                      maxBid: net.priceLimit,
                      increasePercent: net.increasePercentage,
                      contextCoverage: net.coverage
                  }
              }
            : {
                  // если сняли релевантные галочки - то id-фраз не отправляем, так как апдейтить нечего
                  keywordIds: [],
                  // нужно отправлять плейсхолдер, иначе мутация ругается валидацией на осутствие одного из двух полей
                  // searchByTrafficVolume и networkByCoverage оба необязательные, но нужно хотя бы одно из них
                  searchByTrafficVolume: searchPlaceholder
              },
        {
            keywordIds: mcbanner ? mcbannerPhrasesIds : [],
            searchByTrafficVolume: mcbanner
                ? {
                      maxBid: mcbanner.priceLimit,
                      setMaximum: true,
                      targetTrafficVolume: 100,
                      increasePercentage: mcbanner.increasePercentage
                  }
                : searchPlaceholder
        }
    ] as [GdSetAutoBidsInput, GdSetAutoBidsInput];
}

export function mapPhraseChangeToApiFormat(phraseChange: PhraseChange): GdUpdateKeywordsItemInput[] {
    let keywords;

    if (phraseChange.phrase && phraseChange.minusWords) {
        keywords = mapPhraseWithMinusWordsToString(phraseChange.phrase, phraseChange.minusWords);
    }

    if (!phraseChange.phrase && phraseChange.minusWords && phraseChange.minusWords.length !== 0) {
        keywords = `-${phraseChange.minusWords.join(' -')}`;
    }

    return [
        {
            id: String(phraseChange.id),
            keyword: keywords || null
        }
    ];
}

export function mapPhraseBidChangeToApiFormat(phraseBidChange: PhraseBidChange): GdSetBidsInput {
    return {
        showConditionIds: [phraseBidChange.id],
        autobudgetPriority: phraseBidChange.autobudgetPriority
            ? mapAutobudgetPriorityTypeToPerlApiFormat(phraseBidChange.autobudgetPriority)
            : null,
        exactPriceContext: phraseBidChange.exactPriceContext ? String(phraseBidChange.exactPriceContext) : null,
        exactPriceSearch: phraseBidChange.exactPriceSearch ? String(phraseBidChange.exactPriceSearch) : null
    };
}

export function mapRefineMinusWordsResponseToAdjustmentItems({
    refineKeyword
}: RefinePhraseQuery): { hasMore: boolean; phrases: PhraseAdjustmentItem[] } {
    const { words, hasMore } = refineKeyword;
    const wordList: PhraseAdjustmentItem[] = [];

    const phraseAdjustmentItems = words
        ? words.reduce((acc, el) => {
              if (el) {
                  acc.push({
                      checked: false,
                      minusWord: el.word ? el.word : '',
                      shows: Number(el.count),
                      requestExample: el.phrases ? el.phrases.slice(0, 5).join(', ') : ''
                  });
              }
              return acc;
          }, wordList)
        : wordList;

    return {
        hasMore,
        phrases: phraseAdjustmentItems
    };
}
